import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchFeatureDocs,
  fetchRuleEngineDecisionTrace,
  storeAnalyticsSelector,
} from "../../../../../../redux/slices/store_analytics";

import IrisDecisionTreeView from "./IrisDecisionTreeView";

export default function IrisDecisionTreeModal({
  open,
  onClose,
  asin,
  executionId,
  store,
  marketplace,
}) {
  const dispatch = useDispatch();
  const {
    ruleEngineDecisionTraceData,
    ruleEngineDecisionTraceLoading,
    ruleEngineDecisionTraceError,
    featureDocs,
  } = useSelector(storeAnalyticsSelector);

  const traceApiData = ruleEngineDecisionTraceData[asin];

  useEffect(() => {
    if (open) {
      dispatch(fetchFeatureDocs(store));
      if (!traceApiData && !ruleEngineDecisionTraceLoading) {
        dispatch(
          fetchRuleEngineDecisionTrace({
            store,
            asin,
            marketplace,
            executionId,
          })
        );
      }
    }
  }, [open, asin]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "90vw",
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          px: 2,
          borderBottom: "1px solid #e2e8f0",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span style={{ fontSize: 18 }}>🤖</span>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, lineHeight: 1.2, color: "#1e3a5f" }}
            >
              Iris Decision Tree
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#94a3b8", fontFamily: "monospace" }}
            >
              {asin}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#94a3b8" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          flex: 1,
          display: "flex",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {ruleEngineDecisionTraceLoading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress size={36} sx={{ color: "#2563eb" }} />
            <Typography variant="body2" sx={{ color: "#94a3b8" }}>
              Loading decision trace…
            </Typography>
          </Box>
        )}

        {ruleEngineDecisionTraceError && !ruleEngineDecisionTraceLoading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography
              variant="body1"
              sx={{ color: "#dc2626", fontWeight: 600 }}
            >
              Failed to load decision trace
            </Typography>
            <Typography variant="body2" sx={{ color: "#94a3b8" }}>
              {ruleEngineDecisionTraceError}
            </Typography>
          </Box>
        )}

        {traceApiData && !ruleEngineDecisionTraceLoading && (
          <IrisDecisionTreeView
            traceApiData={traceApiData}
            featureDocs={featureDocs}
            asin={asin}
            store={store}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
